import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Warning as WarningIcon,
  PriorityHigh as PriorityHighIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  SmartToy as SmartToyIcon,
  Speed as SpeedIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { TickIcon, LockUnlockIcon, ShareIcon } from '../components/icons/SvgIcons';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import useWebSocket from '../hooks/useWebSocket';
import debugService from '../services/debugService';  // 🐛 NEW: Debug Service
import { useAuth } from '../contexts/AuthContext'; // ✅ NEW: Add authentication context
import batchRequestController from '../services/BatchRequestController'; // 🚀 NEW: Centralized batch controller

// 🚨 VIP EMAIL CONFIGURATION - Αυτά τα emails θα εμφανίζονται με πορτοκαλί animation + sound
const VIP_EMAILS = [
  'info@digital-dentistry.gr',      // 🎯 VIP Email #1 - Digital Dentistry
  'tourism@merimnaseminars.gr'      // 🎯 VIP Email #2 - Tourism Department
];

// 🔊 Global Audio Context for VIP alerts (auto-initialized on any user interaction)
let globalAudioContext = null;
let audioInitAttempted = false;

// 🔊 Auto-initialize Audio Context on any user interaction
const ensureAudioContext = (setAudioReadyCallback) => {
  if (!globalAudioContext && !audioInitAttempted) {
    try {
      globalAudioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioInitAttempted = true;
      debugService.log('🔊 Audio Context auto-initialized - VIP alerts now ready!');
      
      // Notify React component that audio is ready
      if (setAudioReadyCallback) {
        setAudioReadyCallback(true);
      }
      
      return true;
    } catch (error) {
      console.warn('⚠️ Audio Context auto-initialization failed:', error.message);
      audioInitAttempted = true;
      return false;
    }
  }
  return !!globalAudioContext;
};

// 🔊 Audio Alert Function για VIP connections (WITH RETRY on audio ready)
const playVipAlert = (retryCallback = null) => {
  try {
    debugService.log(`🔊 PLAY VIP ALERT: Starting...`);
    debugService.log(`🔊 PLAY VIP ALERT: globalAudioContext = `, globalAudioContext);
    debugService.log(`🔊 PLAY VIP ALERT: globalAudioContext.state = `, globalAudioContext?.state);
    
    // Try to ensure audio context is ready
    if (!ensureAudioContext()) {
      console.warn('⚠️ Audio context not available - VIP alert will play after first user click');
      return false;
    }
    
    // Resume context if suspended (browser policy)
    if (globalAudioContext.state === 'suspended') {
      debugService.log(`🔊 RESUMING suspended audio context...`);
      globalAudioContext.resume();
    }
    
    const oscillator = globalAudioContext.createOscillator();
    const gainNode = globalAudioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(globalAudioContext.destination);
    
    // VIP Alert Sound: Διπλό beep με υψηλότερο volume
    oscillator.frequency.setValueAtTime(800, globalAudioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, globalAudioContext.currentTime + 0.2);
    oscillator.frequency.setValueAtTime(800, globalAudioContext.currentTime + 0.4);
    
    // Increased volume from 0.3 to 0.7
    gainNode.gain.setValueAtTime(0.7, globalAudioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, globalAudioContext.currentTime + 0.6);
    
    oscillator.start(globalAudioContext.currentTime);
    oscillator.stop(globalAudioContext.currentTime + 0.6);
    
    debugService.log('🚨🔊 VIP Alert Sound PLAYED! Volume: 0.7, Duration: 0.6s');
    
    // Clear any pending retry
    if (retryCallback) {
      retryCallback(null);
    }
    
    return true;
  } catch (error) {
    console.error('⚠️❌ Audio alert FAILED:', error.message, error);
    return false;
  }
};

// 🔊 CONTINUOUS VIP Alert - Starts repeating sound loop (with retry support)
const startVipAlertLoop = (setIntervalFunction, setPendingAlertCallback = null, showModalCallback = null) => {
  // Try to play initial sound immediately
  const initialSuccess = playVipAlert();
  
  if (!initialSuccess && setPendingAlertCallback) {
    // Audio not ready - set up pending alert
    debugService.log('🔊 VIP Alert pending - will start when user interacts with page');
    setPendingAlertCallback(() => startVipAlertLoop(setIntervalFunction, null, null));
    
    // 🚨 NEW: Show modal immediately when VIP teacher enters and audio not ready
    if (showModalCallback) {
      debugService.log('🚨 VIP teacher detected! Showing audio permission modal...');
      showModalCallback(true);
    }
    
    return;
  }
  
  // Start repeating every 3 seconds
  const intervalId = setInterval(() => {
    playVipAlert();
    debugService.log('🔄 VIP Alert Loop - Sound repeated');
  }, 3000);
  
  setIntervalFunction(intervalId);
  debugService.log('🚨 VIP CONTINUOUS ALERT STARTED - Playing every 3 seconds');
};

// 🔇 Stop VIP Alert Loop
const stopVipAlertLoop = (intervalId, setIntervalFunction) => {
  if (intervalId) {
    clearInterval(intervalId);
    setIntervalFunction(null);
    debugService.log('✋ VIP CONTINUOUS ALERT STOPPED');
  }
};

// ENHANCED API with Rate Limiting & Bot-First Strategy + Group Matching
const enhancedApi = {
  // 🤖 BOT SYSTEM API - NOW USES BATCH CONTROLLER (prevents 429 errors)
  getBotParticipants: async (meetingId, priority = 5) => {
    try {
      // Use batch controller instead of direct API call
      const data = await batchRequestController.requestParticipants(
        meetingId,
        priority,
        () => api.get(`/api/bot/meetings/${meetingId}/participants`, {
          timeout: 12000,
        })
      );
      
      // Return in expected format
      return { data };
    } catch (error) {
      // 🔄 Track 429 errors for state cleanup
      if (error.response?.status === 429) {
        console.error('⚠️ 429 Rate Limit in getBotParticipants');
        localStorage.setItem('lastRateLimitError', Date.now().toString());
      }
      
      if (error.code === 'ECONNABORTED') {
        console.log(`⏰ Bot API timeout for meeting ${meetingId}`);
      }
      throw error;
    }
  },

  // 📚 BOT SYSTEM API - NOW USES BATCH CONTROLLER (prevents 429 errors)
  getBotGroupStudents: async (groupId, priority = 5) => {
    try {
      // Use batch controller instead of direct API call
      const data = await batchRequestController.requestGroupStudents(
        groupId,
        priority,
        () => api.get(`/api/bot/groups/${groupId}/students?limit=1000`, {
          timeout: 10000,
        })
      );
      
      // Return in expected format
      return { data };
    } catch (error) {
      // 🔄 Track 429 errors for state cleanup
      if (error.response?.status === 429) {
        console.error('⚠️ 429 Rate Limit in getBotGroupStudents');
        localStorage.setItem('lastRateLimitError', Date.now().toString());
      }
      
      if (error.code === 'ECONNABORTED') {
        console.log(`⏰ Bot groups API timeout for group ${groupId}`);
      }
      throw error;
    }
  },

  // 🤖 BOT-ONLY: NO fallback APIs - ONLY Bot System endpoints
  // All API calls MUST go through /api/bot/* to prevent 429 errors
  getLiveMeetings: async () => {
    debugService.log('🤖 BOT-ONLY: Getting meetings from Bot System...');
    
    try {
      // STEP 1: Check if Bot System is healthy
      const healthCheck = await api.get('/api/bot/health', { timeout: 3000 });
      if (!healthCheck.data?.success) {
        throw new Error('Bot System unhealthy');
      }
      
      // STEP 2: Get meetings from Bot System ONLY (no fallback to /api/meetings/live)
      const botResponse = await api.get('/api/bot/meetings/active', { timeout: 8000 });
      if (botResponse.data?.success && botResponse.data?.data?.length >= 0) {
        debugService.log('✅ BOT-ONLY: Got meetings from Bot System:', botResponse.data.data.length);
        
        const processedMeetings = botResponse.data.data.map(meeting => {
          if (!meeting.matched_group_id && meeting.meeting_name) {
            const groupNameFromTitle = meeting.meeting_name.split(' / ')[0]?.trim();
            if (groupNameFromTitle) {
              meeting.extracted_group_name = groupNameFromTitle;
              debugService.log(`🔗 BOT: Extracted group name: "${groupNameFromTitle}"`);
            }
          }
          return meeting;
        });
        
        debugService.log('🤖 BOT-ONLY: Processed meetings:', processedMeetings.length);
        return processedMeetings;
      }
      
      throw new Error('Bot System returned invalid response');
      
    } catch (error) {
      debugService.log(`❌ BOT-ONLY: Failed to get meetings: ${error.message}`);
      console.error('🤖 BOT-ONLY: No fallback available - returning empty array to prevent 429 errors');
      
      // 🤖 BOT-ONLY: NO FALLBACK - Return empty array to prevent /api/meetings/live calls
      return [];
    }
  },

  // 🤖 BOT-ONLY: Auto-match group by extracted name
  findGroupByName: async (groupName) => {
    try {
      debugService.log(`🔍 BOT-ONLY: Searching for group: "${groupName}"`);
      
      const botResponse = await api.get(`/api/bot/groups/search?search=${encodeURIComponent(groupName)}`, {
        timeout: 5000
      });
      
      if (botResponse.data?.success && botResponse.data?.data) {
        const match = botResponse.data.data;
        debugService.log(`✅ BOT-ONLY: Found group match: "${match.group_name}" (ID: ${match.id}) - ${botResponse.data.match_type}`);
        return match;
      }
      
      // Bot found no match
      if (botResponse.data?.success === false) {
        debugService.log(`❌ BOT-ONLY: No group match found for: "${groupName}"`);
        if (botResponse.data.available_groups?.length > 0) {
          debugService.log(`🔍 Available groups:`, botResponse.data.available_groups.slice(0, 5));
        }
      }
      
      return null;
      
    } catch (error) {
      debugService.log(`❌ BOT-ONLY: Group search failed: ${error.message}`);
      console.error('🤖 BOT-ONLY: No fallback available - returning null to prevent additional API calls');
      return null;
    }
  },

  // ✅ NEW: Meeting Assignment APIs for shared real-time assignments
  assignMeeting: async (meetingId) => {
    try {
      debugService.log(`📌 Assigning meeting ${meetingId} to current user`);
      const response = await api.post(`/api/meetings/${meetingId}/assign`);
      return response.data;
    } catch (error) {
      console.error('Failed to assign meeting:', error);
      throw error;
    }
  },

  unassignMeeting: async (meetingId) => {
    try {
      debugService.log(`📌 Unassigning meeting ${meetingId} from current user`);
      const response = await api.delete(`/api/meetings/${meetingId}/assign`);
      return response.data;
    } catch (error) {
      console.error('Failed to unassign meeting:', error);
      throw error;
    }
  },

  getMeetingAssignments: async () => {
    try {
      debugService.log(`🔗 Getting all meeting assignments`);
      const response = await api.get(`/api/meetings/assignments`);
      return response.data;
    } catch (error) {
      console.error('Failed to get meeting assignments:', error);
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      debugService.log(`👤 Getting current user profile`);
      const response = await api.get(`/api/users/profile`);
      debugService.log(`👤 Profile API response:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to get current user:', error);
      debugService.log('❌ getCurrentUser error details:', error.response?.data || error.message);
      throw error;
    }
  },

  // 🆕 NEW: Get all users with their Zoom API info
  getUsersZoomApiInfo: async () => {
    try {
      debugService.log('📊 Getting users Zoom API info...');
      const response = await api.get('/api/user-zoom-api/all');
      return response.data;
    } catch (error) {
      console.error('Failed to get users Zoom API info:', error);
      throw error;
    }
  }
};

// 🚀 BATCH CONTROLLER: Caching and rate limiting now handled by BatchRequestController
// No need for local cache - batch controller provides centralized caching with TTL

function SimpleLiveMonitoring() {
  // ✅ NEW: Use AuthContext for authentication
  const { user, token, loading: authLoading } = useAuth();
  
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [gridColumns, setGridColumns] = useState(3); // 2, 3, or 4 columns
  const [systemStatus, setSystemStatus] = useState({
    botSystem: false,
    totalRequests: 0,
    cacheHits: 0
  });
  
  // ✅ NEW: Meeting Assignment State for real-time shared assignments
  const [assignedMeetings, setAssignedMeetings] = useState(new Set()); // Meeting IDs assigned by current user
  const [meetingAssignments, setMeetingAssignments] = useState({}); // Meeting ID -> Array of assigned users (shared between all users)
  const [assignmentsLoading, setAssignmentsLoading] = useState(false); // Loading state for assignments
  // ✅ REMOVED: const [currentUser, setCurrentUser] = useState(null); // Use AuthContext user instead

  // 🔊 NEW: Audio Ready State
  const [audioReady, setAudioReady] = useState(() => {
    // Initialize from localStorage
    const storedAudio = localStorage.getItem('vip-audio-enabled');
    return storedAudio === 'true';
  });
  const [pendingVipAlert, setPendingVipAlert] = useState(null);
  const [showAudioPermissionModal, setShowAudioPermissionModal] = useState(false);

  // 📱 SMS State
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [sendingSms, setSendingSms] = useState(false);
  const [sendingTemplateId, setSendingTemplateId] = useState(null);

  // 🚨 NEW: Global VIP Tracking State
  const [globalVipTracking, setGlobalVipTracking] = useState({
    previousVipEmails: new Set(),
    currentVipMeetings: new Map(), // meetingId -> vipTeachers[]
    activeNotification: null,
    audioInterval: null
  });

  // ⚙️ NEW: User Refresh Interval Preference (14s/18s/21s/28s)
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const stored = localStorage.getItem('refresh-interval-preference');
    return stored ? parseInt(stored, 10) : 14000; // Default: 14 seconds
  });

  // 🆕 NEW: Users Zoom API Info State
  const [usersZoomApiInfo, setUsersZoomApiInfo] = useState([]);
  const [zoomApiInfoLoading, setZoomApiInfoLoading] = useState(false);

  // ✅ Optimized: Immediate state update without waiting for API calls
  const handleRefreshIntervalChange = useCallback((newInterval) => {
    // Immediate update - don't wait for anything
    setRefreshInterval(newInterval);
    localStorage.setItem('refresh-interval-preference', newInterval.toString());
    debugService.log(`⚙️ Refresh interval changed to: ${newInterval}ms`);
  }, []);

  // REAL-TIME: Dynamic refresh interval based on user preference
  const { data: meetings = [], isLoading, error, refetch } = useQuery(
    'liveMeetings',
    enhancedApi.getLiveMeetings,
    {
      refetchInterval: refreshInterval, // ✅ User controllable: 14s/18s/21s/28s
      refetchOnWindowFocus: false,
      staleTime: refreshInterval - 1000, // Slightly less than refetch interval
      cacheTime: 5 * 60 * 1000, // ✅ NEW: Keep data in cache for 5 minutes after unmount
      keepPreviousData: true, // ✅ NEW: Show cached data immediately while fetching new data
      retry: 2
    }
  );

  // WebSocket for real-time updates (auto-detect URL from current origin)
  const { connectionStatus, lastMessage, emit: socketEmit } = useWebSocket('', {
    shouldReconnect: () => true,
    reconnectAttempts: 3,
    reconnectInterval: 5000
  });

  // Handle WebSocket messages with rate limiting
  useEffect(() => {
    if (lastMessage && lastMessage.data) {
      try {
        // lastMessage.data is already parsed object, not JSON string
        const message = lastMessage.data;
        debugService.log('📨 WebSocket message:', lastMessage.event, message);
        
        if (lastMessage.event === 'participant_update' || 
            lastMessage.event === 'meeting_update' ||
            message.type === 'participant_update' || 
            message.type === 'meeting_update') {
          // Debounced update
          const timer = setTimeout(() => {
            setLastUpdate(new Date());
            refetch();
          }, 2000); // 2 second debounce
          
          return () => clearTimeout(timer);
        }
        
        // ✅ NEW: Handle real-time assignment updates
        if (lastMessage.event === 'meetingAssignmentChanged') {
          const { meetingId, userId, action, userInfo } = message;
          
          debugService.log(`📌 Real-time assignment update: ${action} for meeting ${meetingId} by user ${userId}`, userInfo);
          
          // ✅ SIMPLE APPROACH: Just reload all assignments from API for consistency
          setTimeout(async () => {
            try {
              debugService.log('🔄 Reloading assignments due to WebSocket update...');
              const assignmentsResponse = await enhancedApi.getMeetingAssignments();
              if (assignmentsResponse.success && assignmentsResponse.data) {
                const assignments = assignmentsResponse.data;
                
                // Process assignments into state
                const userAssignedMeetings = new Set();
                const allMeetingAssignments = {};
                
                Object.keys(assignments).forEach(meetingId => {
                  const assignedUsers = assignments[meetingId];
                  allMeetingAssignments[meetingId] = assignedUsers;
                  
                  // Check if current user is assigned to this meeting
                  if (user && assignedUsers.some(assignedUser => assignedUser.id === (user.userId || user.id))) {
                    userAssignedMeetings.add(parseInt(meetingId));
                  }
                });
                
                setAssignedMeetings(userAssignedMeetings);
                setMeetingAssignments(allMeetingAssignments);
                
                debugService.log('✅ Assignments reloaded from API due to WebSocket update');
              }
            } catch (error) {
              console.error('❌ Failed to reload assignments:', error);
            }
          }, 500); // Small delay to ensure API is updated
        }
      } catch (e) {
        console.warn('Invalid WebSocket message:', e, lastMessage);
      }
    }
  }, [lastMessage, refetch, user]);

  // Enhanced System status monitoring
  useEffect(() => {
    const checkBotSystem = async () => {
      try {
        const response = await api.get('/api/bot/health', { timeout: 3000 });
        const batchStats = batchRequestController.getStats();
        setSystemStatus(prev => ({
          ...prev,
          botSystem: response.data?.success === true,
          totalRequests: prev.totalRequests + 1,
          cacheHits: batchStats.cacheSize
        }));
      } catch (error) {
        console.log('⚠️ Bot System unavailable:', error.message);
        const batchStats = batchRequestController.getStats();
        setSystemStatus(prev => ({
          ...prev,
          botSystem: false,
          totalRequests: prev.totalRequests + 1,
          cacheHits: batchStats.cacheSize
        }));
      }
    };
    
    // Check immediately
    checkBotSystem();
    
    // PRODUCTION: Check every 30 seconds for status (was 5s - too aggressive)
    const interval = setInterval(checkBotSystem, 30000);
    return () => clearInterval(interval);
  }, []);

  // 🆕 NEW: Load Users Zoom API Info
  useEffect(() => {
    const loadZoomApiInfo = async () => {
      try {
        setZoomApiInfoLoading(true);
        const response = await enhancedApi.getUsersZoomApiInfo();
        if (response.success && response.data) {
          setUsersZoomApiInfo(response.data);
          debugService.log(`📊 Loaded Zoom API info for ${response.data.length} users`);
        }
      } catch (error) {
        console.error('Failed to load Zoom API info:', error);
        setUsersZoomApiInfo([]);
      } finally {
        setZoomApiInfoLoading(false);
      }
    };

    // Load immediately
    loadZoomApiInfo();

    // Reload every 60 seconds to keep info fresh
    const interval = setInterval(loadZoomApiInfo, 60000);
    return () => clearInterval(interval);
  }, []);

  // ✅ NEW: Load assignments from API when user is available
  useEffect(() => {
    const loadAssignments = async () => {
      if (!user || !token || authLoading) {
        console.log('🔍 Waiting for authentication...', { 
          hasUser: !!user,
          hasToken: !!token,
          authLoading 
        });
        return;
      }
      
      // Skip firstname/lastname check - not critical for assignments loading
      if (!user.id || !user.role) {
        console.log('🔍 Waiting for user authentication...', user);
        setTimeout(loadAssignments, 500); // Retry after 500ms
        return;
      }
      
      try {
        setAssignmentsLoading(true);
        debugService.log('🔍 Loading SHARED assignments from API for user:', user);
        
        // Load meeting assignments from API
        const assignmentsResponse = await enhancedApi.getMeetingAssignments();
        if (assignmentsResponse.success && assignmentsResponse.data) {
          const assignments = assignmentsResponse.data;
          
          // Process assignments into state
          const userAssignedMeetings = new Set();
          const allMeetingAssignments = {};
          
          Object.keys(assignments).forEach(meetingId => {
            const assignedUsers = assignments[meetingId];
            allMeetingAssignments[meetingId] = assignedUsers;
            
            // Check if current user is assigned to this meeting
            if (user && assignedUsers.some(assignedUser => assignedUser.id === user.id)) {
              userAssignedMeetings.add(parseInt(meetingId));
            }
          });
          
          setAssignedMeetings(userAssignedMeetings);
          setMeetingAssignments(allMeetingAssignments);
          
          debugService.log('🔗 SHARED assignments loaded from API:', {
            userAssigned: Array.from(userAssignedMeetings),
            allAssignments: allMeetingAssignments,
            currentUserId: user.id
          });
        }
      } catch (error) {
        console.error('Failed to load assignments from API:', error);
        // Initialize empty state on error
        setAssignedMeetings(new Set());
        setMeetingAssignments({});
      } finally {
        setAssignmentsLoading(false);
      }
    };

    loadAssignments();
  }, [user, token, authLoading, socketEmit]); // Add socketEmit to deps
  
  // ✅ NEW: Cleanup assignments for ended meetings (API-based)
  useEffect(() => {
    if (!Array.isArray(meetings) || meetings.length === 0 || assignmentsLoading) return;
    
    const currentMeetingIds = new Set(meetings.map(m => m.id));
    const assignedMeetingIds = [...assignedMeetings];
    const needsCleanup = assignedMeetingIds.some(id => !currentMeetingIds.has(id));
    
    if (needsCleanup) {
      console.log('🧹 Cleaning up assignments for ended meetings...');
      
      // Remove assignments for meetings that no longer exist (local state only)
      const validAssignedMeetings = assignedMeetingIds.filter(id => currentMeetingIds.has(id));
      const cleanAssignments = {};
      
      Object.keys(meetingAssignments).forEach(meetingId => {
        if (currentMeetingIds.has(parseInt(meetingId))) {
          cleanAssignments[meetingId] = meetingAssignments[meetingId];
        }
      });
      
      setAssignedMeetings(new Set(validAssignedMeetings));
      setMeetingAssignments(cleanAssignments);
      
      // Note: Backend will handle database cleanup automatically
      debugService.log(`🧹 Cleaned up ${assignedMeetingIds.length - validAssignedMeetings.length} ended meeting assignments from local state`);
    }
  }, [meetings, assignedMeetings, meetingAssignments, assignmentsLoading]); // Cleanup when meetings change

  // ✅ ENHANCED: Filter and sort active meetings (assigned meetings first)
  const activeMeetings = useMemo(() => {
    debugService.log('🔥 DEBUGGING: meetings variable:', meetings, 'Type:', typeof meetings, 'Is array?', Array.isArray(meetings));
    if (!Array.isArray(meetings)) {
      console.error('🚨 ERROR: meetings is not an array!', meetings);
      return [];
    }
    
    const active = meetings.filter(meeting => meeting.status === 'active');
    
    // Sort meetings: assigned by current user first, then others
    const sorted = active.sort((a, b) => {
      const aAssigned = assignedMeetings.has(a.id);
      const bAssigned = assignedMeetings.has(b.id);
      
      // Assigned meetings come first
      if (aAssigned && !bAssigned) return -1;
      if (!aAssigned && bAssigned) return 1;
      
      // If both assigned or both not assigned, maintain original order
      return 0;
    });
    
    debugService.log('🔗 Meetings sorted by assignment status:', {
      total: sorted.length,
      assigned: sorted.filter(m => assignedMeetings.has(m.id)).length,
      unassigned: sorted.filter(m => !assignedMeetings.has(m.id)).length
    });
    
    return sorted;
  }, [meetings, assignedMeetings]);

  // 🔄 NEW: Circuit Breaker Monitor & Auto-Recovery
  useEffect(() => {
    const monitorInterval = setInterval(() => {
      const stats = batchRequestController.getStats();
      
      // Check if circuit breaker has been triggered
      if (stats.circuitBreaks > 0) {
        debugService.log(`⚡ Circuit Breaker Active - ${stats.errors429} 429 errors detected`);
        
        // Check if we're in backoff period
        const isOpen = batchRequestController.isCircuitOpen();
        
        if (!isOpen && stats.errors429 > 0) {
          debugService.log('🔄 Circuit breaker closed - clearing error cache for fresh start');
          batchRequestController.clearErrorCache();
        }
      }
      
      // Auto-cleanup: Clear old error cache entries periodically
      if (stats.cacheCleanups === 0 && stats.errors429 > 3) {
        debugService.log('🧹 Auto-cleanup: Clearing error cache due to multiple 429 errors');
        batchRequestController.clearErrorCache();
      }
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(monitorInterval);
  }, []);

  // 🔄 NEW: State Recovery on Mount (Clear any stale error state)
  useEffect(() => {
    // On component mount, ensure clean state
    const stats = batchRequestController.getStats();
    
    if (stats.errors429 > 0 || stats.cacheSize > 50) {
      debugService.log('🔄 Component mounted - performing state cleanup');
      batchRequestController.clearErrorCache();
    }
    
    // Clear any stale data from localStorage that might cause issues
    try {
      // Check for any corrupt state indicators
      const lastErrorTime = localStorage.getItem('lastRateLimitError');
      if (lastErrorTime) {
        const timeSinceError = Date.now() - parseInt(lastErrorTime);
        if (timeSinceError < 60000) { // Less than 1 minute ago
          debugService.log('⚠️ Recent rate limit error detected - clearing caches');
          batchRequestController.clearCache();
        }
        localStorage.removeItem('lastRateLimitError');
      }
    } catch (e) {
      debugService.log('Error checking localStorage:', e);
    }
  }, []); // Run once on mount

  // 🔊 Show Audio Permission Modal on page load (after a short delay) - only once per session
  useEffect(() => {
    const timer = setTimeout(() => {
      const hasSeenModal = localStorage.getItem('vip-audio-modal-seen');
      
      if (!audioReady && !audioInitAttempted && !hasSeenModal) {
        setShowAudioPermissionModal(true);
      }
    }, 2000); // Show after 2 seconds
    
    return () => clearTimeout(timer);
  }, [audioReady]);

  // 🔊 Handle Audio Permission Grant
  const handleAudioPermissionGrant = () => {
    const success = ensureAudioContext(setAudioReady);
    
    if (success) {
      // Audio is now ready - trigger any pending VIP alerts
      if (pendingVipAlert) {
        debugService.log('Audio ready! Starting pending VIP alert...');
        pendingVipAlert();
        setPendingVipAlert(null);
      }
      
      // Mark modal as seen
      localStorage.setItem('vip-audio-modal-seen', 'true');
      localStorage.setItem('vip-audio-enabled', 'true');
      
      setShowAudioPermissionModal(false);
      debugService.log('🔊 Audio permissions granted by user via modal');
    }
  };

  // 🔊 Handle Audio Permission Skip
  const handleAudioPermissionSkip = () => {
    // Mark modal as seen but audio not enabled
    localStorage.setItem('vip-audio-modal-seen', 'true');
    localStorage.setItem('vip-audio-enabled', 'false');
    
    setShowAudioPermissionModal(false);
    debugService.log('🔇 Audio permissions skipped by user via modal');
  };


  // 🔊 Global Click Handler για Audio Context initialization + Pending Alerts (fallback)
  useEffect(() => {
    const handleFirstClick = () => {
      if (!audioReady) {
        const success = ensureAudioContext(setAudioReady);
        
        if (success) {
          // Audio is now ready - trigger any pending VIP alerts
          if (pendingVipAlert) {
            debugService.log('Audio ready! Starting pending VIP alert...');
            pendingVipAlert();
            setPendingVipAlert(null);
          }
          
          // Close modal if still open
          setShowAudioPermissionModal(false);
          
          // Only need to initialize once
          document.removeEventListener('click', handleFirstClick, true);
        }
      }
    };
    
    // Add listener for any click on the page (fallback if modal is skipped)
    document.addEventListener('click', handleFirstClick, true);
    
    return () => {
      document.removeEventListener('click', handleFirstClick, true);
    };
  }, [pendingVipAlert, audioReady]);

  // 🔊 NEW: Auto-restore Audio Context on page load if previously enabled
  useEffect(() => {
    const storedAudio = localStorage.getItem('vip-audio-enabled');
    if (storedAudio === 'true' && !audioReady) {
      // Try to restore audio context
      setTimeout(() => {
        const success = ensureAudioContext(setAudioReady);
        if (success) {
          debugService.log('🔊 Audio context automatically restored from localStorage');
        }
      }, 1000); // Small delay to allow page to load
    }
  }, []); // Run only once on mount

  // 🚨 NEW: Global VIP Alert Handler
  const handleNewVipAlert = useCallback((newVipEmails, vipMeetingsMap) => {
    const firstVip = newVipEmails[0];
    
    // Find which meeting has this VIP
    let vipInfo = null;
    let meetingName = '';
    
    vipMeetingsMap.forEach((vips, meetingId) => {
      const found = vips.find(v => {
        const email = v.email || v.participant_email;
        return email?.toLowerCase() === firstVip;
      });
      
      if (found) {
        vipInfo = found;
        const meeting = activeMeetings.find(m => m.id === meetingId);
        meetingName = meeting?.resolved_group_name || meeting?.meeting_name || `Meeting ${meetingId}`;
      }
    });
    
    const vipDisplayName = vipInfo?.full_name || 
                           vipInfo?.user_name ||
                           vipInfo?.participant_name ||
                           'VIP Teacher';
    
    // Show global notification
    setGlobalVipTracking(prev => ({
      ...prev,
      activeNotification: {
        show: true,
        vipName: vipDisplayName,
        vipEmail: firstVip,
        meetingName: meetingName,
        timestamp: new Date()
      }
    }));
    
    // Start audio alert with proper context
    debugService.log(`🔊 VIP ALERT: audioReady = ${audioReady}`);
    debugService.log(`🔊 VIP ALERT: globalAudioContext = `, globalAudioContext);
    
    if (audioReady) {
      // 🛡️ Prevent duplicate intervals - stop existing one first
      if (globalVipTracking.audioInterval) {
        debugService.log('⚠️ PREVENTING DUPLICATE: Stopping existing VIP alert before starting new one');
        clearInterval(globalVipTracking.audioInterval);
      }
      
      debugService.log(`🚨 STARTING VIP ALERT LOOP for ${vipDisplayName}`);
      startVipAlertLoop(
        (intervalId) => {
          debugService.log(`🔄 VIP Alert interval ID set: ${intervalId}`);
          setGlobalVipTracking(prev => ({...prev, audioInterval: intervalId}));
        },
        setPendingVipAlert,
        setShowAudioPermissionModal
      );
    } else {
      // Use setTimeout to avoid render-time state update
      setTimeout(() => {
        setPendingVipAlert(() => {
          // Create a simple callback to avoid infinite loops
          return () => console.log('VIP alert pending');
        });
        setShowAudioPermissionModal(true);
      }, 0);
    }
  }, [activeMeetings, audioReady, startVipAlertLoop, setPendingVipAlert, setShowAudioPermissionModal]);

  // 🔍 NEW: Detect VIPs in a specific meeting
  const detectVipsInMeeting = useCallback((participants, meeting) => {
    const VIP_EMAILS = [
      'info@digital-dentistry.gr',
      'tourism@merimnaseminars.gr'
    ];
    
    if (!Array.isArray(participants)) {
      debugService.log(`⚠️ Participants is not an array for meeting ${meeting.id}:`, participants);
      return [];
    }
    
    debugService.log(`🔍 VIP DETECTION: Checking ${participants.length} participants in meeting ${meeting.id}`);
    
    const vipTeachers = participants.filter(participant => {
      // Try multiple possible email field names from Zoom API
      const participantEmail = participant.email || 
                              participant.participant_email || 
                              participant.user_email || 
                              participant.join_url?.match(/[\w\.-]+@[\w\.-]+/)?.[0]; // Extract email from join_url if present
      
      // Extract name from various possible fields
      const participantName = participant.user_name || 
                              participant.participant_name || 
                              participant.name || 
                              participant.display_name ||
                              'Unknown Participant';
      
      debugService.log(`👤 Checking participant: ${participantName} (${participantEmail})`);
      
      if (!participantEmail) {
        debugService.log(`⚠️ No email found for participant:`, participant);
        return false;
      }
      
      const isVip = VIP_EMAILS.includes(participantEmail.toLowerCase());
      
      if (isVip) {
        debugService.log(`🚨🚨🚨 VIP DETECTED: ${participantName} (${participantEmail}) in meeting ${meeting.id}`);
      }
      
      return isVip;
    });
    
    debugService.log(`🎯 VIP Detection Result: Found ${vipTeachers.length} VIPs in meeting ${meeting.id}`);
    
    return vipTeachers;
  }, []);

  // 🌍 NEW: Global VIP Monitoring Across All Meetings
  const monitorVipTeachers = useCallback((meetings, allParticipantsData) => {
    const allCurrentVips = new Map();
    
    meetings.forEach(meeting => {
      const participants = allParticipantsData[meeting.id] || [];
      const vipTeachersInMeeting = detectVipsInMeeting(participants, meeting);
      
      if (vipTeachersInMeeting.length > 0) {
        // Enhance VIP data with proper names
        const enhancedVips = vipTeachersInMeeting.map(vip => {
          const participantName = vip.user_name || 
                                  vip.participant_name || 
                                  vip.name || 
                                  vip.display_name ||
                                  'Unknown VIP';
          
          const [firstName, ...lastNameParts] = participantName.split(' ');
          
          return {
            ...vip,
            first_name: firstName || 'VIP',
            last_name: lastNameParts.join(' ') || 'Teacher',
            full_name: participantName
          };
        });
        
        allCurrentVips.set(meeting.id, enhancedVips);
        debugService.log(`✅ Meeting ${meeting.id}: ${enhancedVips.length} VIPs found`);
      }
    });
    
    // Check for new VIPs globally
    const currentVipEmails = new Set();
    allCurrentVips.forEach(vips => {
      vips.forEach(vip => {
        const email = vip.email || vip.participant_email;
        if (email) currentVipEmails.add(email.toLowerCase());
      });
    });
    
    const newVips = [...currentVipEmails].filter(
      email => !globalVipTracking.previousVipEmails.has(email)
    );
    
    if (newVips.length > 0) {
      debugService.log(`🚨🚨🚨 NEW VIP DETECTED: ${newVips.join(', ')}`);
      debugService.log(`🚨 VIP MEETINGS MAP:`, allCurrentVips);
      debugService.log(`🚨 NEW VIP ALERT: ${newVips.join(', ')}`);
      handleNewVipAlert(newVips, allCurrentVips);
    } else {
      debugService.log(`ℹ️ No new VIPs - Current: ${Array.from(currentVipEmails).join(', ')}, Previous: ${Array.from(globalVipTracking.previousVipEmails).join(', ')}`);
    }
    
    // 🧹 AUTO-CLEANUP: If no VIPs present, clear any active audio alerts
    if (currentVipEmails.size === 0 && globalVipTracking.audioInterval) {
      debugService.log('🧹 No VIPs present - auto-stopping any active audio alerts');
      clearInterval(globalVipTracking.audioInterval);
      setGlobalVipTracking(prev => ({
        ...prev,
        activeNotification: null,
        audioInterval: null
      }));
    }
    
    // Update global tracking
    setGlobalVipTracking(prev => ({
      ...prev,
      previousVipEmails: currentVipEmails,
      currentVipMeetings: allCurrentVips
    }));
    
    debugService.log(`🌍 VIP Monitoring: ${currentVipEmails.size} VIPs across ${allCurrentVips.size} meetings`);
  }, [globalVipTracking.previousVipEmails, globalVipTracking.audioInterval, detectVipsInMeeting, handleNewVipAlert]);

  // ===== SMS FUNCTIONALITY =====
  
  // Fetch SMS templates on component mount
  useEffect(() => {
    const fetchSmsTemplates = async () => {
      try {
        const response = await api.get('/api/sms/templates');
        if (response.data.success && response.data.templates) {
          setSmsTemplates(response.data.templates);
        }
      } catch (error) {
        console.error('Error fetching SMS templates:', error);
      }
    };
    
    fetchSmsTemplates();
  }, []);

  // Handle student name click to open SMS dialog
  const handleStudentNameClick = useCallback((student) => {
    if (student.phone1) {
      setSelectedStudent(student);
      setSmsDialogOpen(true);
    } else {
      toast.error('Student has no phone number');
    }
  }, []);

  // Send SMS
  const handleSendSms = useCallback(async (templateId) => {
    if (!selectedStudent) return;
    
    try {
      setSendingSms(true);
      setSendingTemplateId(templateId);
      
      const response = await api.post('/api/sms/send', {
        phone: selectedStudent.phone1,
        template_id: templateId,
        student_name: `${selectedStudent.first_name} ${selectedStudent.last_name}`,
        meeting_info: {
          id: selectedMeetingId,
          topic: 'Live Monitoring'
        }
      });
      
      if (response.data.success) {
        toast.success(`📱 SMS sent successfully to ${selectedStudent.first_name}!`, { duration: 3000 });
        setSmsDialogOpen(false);
        setSelectedStudent(null);
      } else {
        toast.error(`Failed to send SMS: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(`Error sending SMS: ${error.response?.data?.error || error.message}`);
    } finally {
      setSendingSms(false);
      setSendingTemplateId(null);
    }
  }, [selectedStudent, selectedMeetingId]);

  // 🔇 NEW: Stop Global VIP Alert
  const stopGlobalVipAlert = useCallback(() => {
    debugService.log('🛑 STOPPING VIP ALERT - intervalId:', globalVipTracking.audioInterval);
    
    // Stop audio if playing using the proper stop function
    if (globalVipTracking.audioInterval) {
      stopVipAlertLoop(
        globalVipTracking.audioInterval, 
        (newId) => setGlobalVipTracking(prev => ({...prev, audioInterval: newId}))
      );
    }
    
    // Clear pending alert
    setPendingVipAlert(null);
    
    // Close notification
    setGlobalVipTracking(prev => ({
      ...prev,
      activeNotification: null,
      audioInterval: null
    }));
    
    debugService.log('✋ Global VIP Alert completely stopped');
  }, [globalVipTracking.audioInterval, setPendingVipAlert]);

  // 🧹 NEW: Cleanup audio intervals on component unmount or page unload
  useEffect(() => {
    const cleanup = () => {
      debugService.log('🧹 CLEANUP: Stopping any active VIP audio intervals');
      if (globalVipTracking.audioInterval) {
        clearInterval(globalVipTracking.audioInterval);
        debugService.log('🧹 CLEANUP: Cleared interval:', globalVipTracking.audioInterval);
      }
    };

    // Cleanup on page unload/navigation
    const handleBeforeUnload = () => {
      cleanup();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Cleanup function for component unmount
    return () => {
      cleanup();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [globalVipTracking.audioInterval]);

  // 🌍 NEW: Global VIP Monitoring Effect - Monitors ALL meetings for VIPs
  useEffect(() => {
    if (!Array.isArray(activeMeetings) || activeMeetings.length === 0) {
      debugService.log('🌍 Global VIP Monitoring: No active meetings to monitor');
      return;
    }

    // 🚀 OPTIMIZED: Sequential participant fetching with UI breathing room (prevents UI blocking)
    const monitorAllMeetings = async () => {
      try {
        const allParticipantsData = {};
        
        // ✅ Sequential processing instead of Promise.all() - avoids UI blocking
        // Each fetch allows UI to process events (clicks, scrolls) between requests
        for (const meeting of activeMeetings) {
          try {
            const response = await enhancedApi.getBotParticipants(meeting.id, 1); // VIP = Priority 1 (highest)
            
            const participantsList = response.data?.participants || [];
            allParticipantsData[meeting.id] = participantsList;
            
            // ✅ CRITICAL: Give UI thread time to process user events (prevents 5s freeze)
            // setTimeout(0) defers execution, allowing clicks/scrolls to be processed
            await new Promise(resolve => setTimeout(resolve, 0));
            
          } catch (error) {
            debugService.log(`⚠️ Failed to fetch participants for meeting ${meeting.id}:`, error.message);
            allParticipantsData[meeting.id] = [];
          }
        }
        
        // ✅ VIP detection runs once with all data (ensures single alert, no duplicates)
        monitorVipTeachers(activeMeetings, allParticipantsData);
        
      } catch (error) {
        debugService.log('🌍 Global VIP Monitoring Error:', error.message);
      }
    };
    
    monitorAllMeetings();
    
    // Run monitoring at user-defined interval for VIP detection (uses batch controller cache)
    const monitoringInterval = setInterval(monitorAllMeetings, refreshInterval); // ✅ User controllable: 14s/18s/21s/28s
    
    return () => clearInterval(monitoringInterval);
  }, [activeMeetings, monitorVipTeachers, enhancedApi, refreshInterval]);

  // ✅ NEW: Meeting Assignment Functions
  const handleAssignMeeting = async (meetingId, event) => {
    if (event) {
      event.stopPropagation(); // Prevent card selection
    }
    
    if (!user) {
      console.warn('No current user available for assignment');
      return;
    }
    
    try {
      const isAssigned = assignedMeetings.has(meetingId);
      
      if (isAssigned) {
        // Unassign meeting - API CALL
        await enhancedApi.unassignMeeting(meetingId);
        
        // Update local state optimistically 
        setAssignedMeetings(prev => {
          const newSet = new Set(prev);
          newSet.delete(meetingId);
          return newSet;
        });
        
        setMeetingAssignments(prev => {
          const newAssignments = { ...prev };
          if (newAssignments[meetingId]) {
            newAssignments[meetingId] = newAssignments[meetingId].filter(assignedUser => assignedUser.id !== user.id);
            if (newAssignments[meetingId].length === 0) {
              delete newAssignments[meetingId];
            }
          }
          return newAssignments;
        });
        
        debugService.log(`✅ Meeting ${meetingId} unassigned via API`);
        
      } else {
        // Assign meeting - API CALL
        await enhancedApi.assignMeeting(meetingId);
        
        // Update local state optimistically
        setAssignedMeetings(prev => new Set([...prev, meetingId]));
        
        setMeetingAssignments(prev => {
          const newAssignments = { ...prev };
          if (!newAssignments[meetingId]) {
            newAssignments[meetingId] = [];
          }
        newAssignments[meetingId].push({
          id: user.userId || user.id,
          username: user.username,                      // ✅ ONLY: Username for display
          profile_color: user.profile_color || '#0015ff' // ✅ Use current user profile color
        });
          return newAssignments;
        });
        
        debugService.log(`✅ Meeting ${meetingId} assigned via API`);
      }
      
      // ✅ NEW: Emit WebSocket event for real-time updates with user info
      if (socketEmit) {
        const assignmentEvent = {
          meetingId,
          userId: user.userId || user.id,
          action: isAssigned ? 'unassigned' : 'assigned',
          userInfo: {
            username: user.username,                    // ✅ ONLY: Username for display
            profile_color: user.profile_color || '#0015ff'
          }
        };
        
        debugService.log('📡 Emitting WebSocket assignment:', assignmentEvent);
        const emitSuccess = socketEmit('meetingAssignmentChanged', assignmentEvent);
        
        if (!emitSuccess) {
          console.warn('📡 WebSocket emit failed - connection status:', connectionStatus);
        }
      } else {
        console.warn('📡 WebSocket emit function not available');
      }
      
    } catch (error) {
      console.error('Failed to toggle meeting assignment:', error);
      // TODO: Show error notification to user
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Live Meetings...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ 
          mb: 2,
          '& .MuiAlert-icon': {
            fontSize: '59px',
            color: 'rgb(186, 191, 193)'
          }
        }}>
          Failed to load meetings: {error.message}
          <IconButton onClick={() => refetch()} size="small" sx={{ ml: 1 }}>
            <RefreshIcon />
          </IconButton>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 🔊 Audio Permission Modal */}
      <Dialog
        open={showAudioPermissionModal}
        onClose={() => setShowAudioPermissionModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #2088ff 0%, #42a5f5 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          fontSize: '1.3rem',
          fontWeight: 'bold'
        }}>
          <Box sx={{ 
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            🔊
          </Box>
          VIP Audio Alerts Ready?
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This system can automatically play sound alerts when VIP teachers join meetings.
          </Typography>
          
          <Alert severity="info" sx={{ 
            mb: 2, 
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            '& .MuiAlert-icon': { 
              color: '#ffeb3b',
              fontSize: '59px'
            }
          }}>
            <Typography variant="body2">
              <strong>VIP Teachers:</strong> {VIP_EMAILS.join(', ')}
            </Typography>
          </Alert>
          
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Browser security requires your permission to play audio. Click "Enable Audio" to activate VIP sound alerts, or "Skip" to continue without sound.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleAudioPermissionSkip}
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Skip for Now
          </Button>
          
          <Button
            onClick={handleAudioPermissionGrant}
            variant="contained"
            startIcon={<VpnKeyIcon />}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s'
            }}
          >
            🔊 Enable Audio Alerts
          </Button>
        </DialogActions>
      </Dialog>

      {/* ENHANCED HEADER - System Status */}
      <Paper elevation={1} sx={{ 
        p: 2, 
        mb: 3, 
        background: 'rgb(0, 57, 108)',
        borderRadius: '12px',
        color: 'rgba(28, 28, 28, 0.87)'
      }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ color: 'rgb(255, 255, 255)', fontWeight: 'bold', fontSize: '1.6rem' }}>
              Live Meeting Monitor
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'rgb(179, 225, 253)', fontSize: '0.85rem', lineHeight: 1.95 }}>
              Real-time Bot-Powered Attendance Tracking
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* ⚙️ NEW: Refresh Interval Selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ color: 'white', minWidth: 'fit-content', fontSize: '0.85rem' }}>
                  Refresh:
                </Typography>
                {[
                  { value: 14000, label: '14s', icon: '⚡' },
                  { value: 18000, label: '18s', icon: '🚴' },
                  { value: 21000, label: '21s', icon: '🏃' },
                  { value: 28000, label: '28s', icon: '🐢' }
                ].map((option) => (
                  <Chip
                    key={option.value}
                    label={`${option.icon} ${option.label}`}
                    size="small"
                    variant={refreshInterval === option.value ? 'filled' : 'outlined'}
                    onClick={() => handleRefreshIntervalChange(option.value)}
                    sx={{ 
                      color: 'white', 
                      borderColor: 'rgba(255,255,255,0.5)',
                      backgroundColor: refreshInterval === option.value ? 'rgba(255,255,255,0.2)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                      fontSize: '0.75rem',
                      height: '26px',
                      borderRadius: '6px'
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item>
                <Chip
                  icon={<SmartToyIcon sx={{ fontSize: '1.1rem' }} />}
                  label={`Bot System: ${systemStatus.botSystem ? 'ON' : 'OFF'}`}
                  color={systemStatus.botSystem ? 'success' : 'warning'}
                  variant="filled"
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold', 
                    fontSize: '0.7rem',
                    height: '26px',
                    borderRadius: '6px',
                    '& .MuiChip-label': { fontSize: '0.7rem' }
                  }}
                />
              </Grid>
              <Grid item>
                <Chip
                  icon={<SpeedIcon sx={{ fontSize: '1.1rem' }} />}
                  label={`Cache: ${systemStatus.cacheHits} items`}
                  color="info"
                  variant="filled"
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: '26px',
                    borderRadius: '6px',
                    backgroundColor: 'rgb(62, 152, 255)',
                    '& .MuiChip-label': { fontSize: '0.7rem' }
                  }}
                />
              </Grid>
              {/* 🔊 NEW: Audio Status Indicator */}
              <Grid item>
                <Chip
                  icon={audioReady ? <VpnKeyIcon sx={{ fontSize: '1.1rem' }} /> : <WarningIcon sx={{ fontSize: '1.1rem' }} />}
                  label={audioReady ? 'Audio Ready' : pendingVipAlert ? '🔇 Click for Sound' : '🔇 Audio Disabled'}
                  color={audioReady ? 'success' : pendingVipAlert ? 'error' : 'default'}
                  variant="filled"
                  onClick={!audioReady ? handleAudioPermissionGrant : undefined}
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: '26px',
                    borderRadius: '6px',
                    cursor: !audioReady ? 'pointer' : 'default',
                    backgroundColor: audioReady ? 'rgba(76, 175, 80, 0.9)' : 
                                    pendingVipAlert ? 'rgba(255, 69, 0, 0.9)' :
                                    'rgba(158, 158, 158, 0.7)',
                    animation: pendingVipAlert ? 'audioAlert 1.5s ease-in-out infinite' : 'none',
                    '& .MuiChip-label': { fontSize: '0.7rem' },
                    '&:hover': !audioReady ? {
                      backgroundColor: 'rgba(255, 152, 0, 0.8)',
                      transform: 'scale(1.05)'
                    } : {},
                    transition: 'all 0.2s',
                    '@keyframes audioAlert': {
                      '0%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.7, transform: 'scale(1.05)' },
                      '100%': { opacity: 1, transform: 'scale(1)' }
                    }
                  }}
                />
              </Grid>
              
            </Grid>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', textAlign: 'right', mt: 0.5 }}>
              Last Update: {lastUpdate.toLocaleTimeString()} • WebSocket: {connectionStatus} • Audio: {audioReady ? 'Ready' : 'Needs Click'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 🆕 MY ZOOM API INFO - Compact display without frame */}
      {!zoomApiInfoLoading && usersZoomApiInfo.length > 0 && (
        <Box sx={{ mb: 2, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold', display: 'block', mb: 0.5 }}>
            My Zoom API: {usersZoomApiInfo.map((userApi, idx) => (
              <Box component="span" key={userApi.id}>
                <Box component="span" sx={{ color: '#2088ff', fontWeight: 'bold' }}>
                  {userApi.username}
                </Box>
                {' - '}
                <Box component="span" sx={{ color: '#666' }}>
                  {userApi.zoomApiLabel || 'Default API'}
                </Box>
                {userApi.apiCallCount > 0 && (
                  <Box component="span" sx={{ 
                    color: '#2e7d32',
                    fontSize: '0.7rem',
                    ml: 0.3
                  }}>
                    ({userApi.apiCallCount} calls)
                  </Box>
                )}
              </Box>
            ))}
          </Typography>
          
          {/* Grid Columns Selector with Dot Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {[2, 3, 4].map((cols) => {
              // Create dot pattern for each column count
              const createDotIcon = (numCols) => {
                const rows = 2;
                const dots = [];
                for (let r = 0; r < rows; r++) {
                  for (let c = 0; c < numCols; c++) {
                    dots.push(<Box key={`${r}-${c}`} sx={{ 
                      width: 4, 
                      height: 4, 
                      borderRadius: '50%', 
                      bgcolor: gridColumns === numCols ? '#2088ff' : '#999' 
                    }} />);
                  }
                }
                return (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: `repeat(${numCols}, 4px)`,
                    gap: '3px'
                  }}>
                    {dots}
                  </Box>
                );
              };
              
              return (
                <IconButton
                  key={cols}
                  onClick={() => setGridColumns(cols)}
                  size="small"
                  sx={{ 
                    padding: '6px',
                    border: gridColumns === cols ? '1px solid #2088ff' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: gridColumns === cols ? 'rgba(32, 136, 255, 0.1)' : 'transparent',
                    '&:hover': { 
                      backgroundColor: gridColumns === cols ? 'rgba(32, 136, 255, 0.2)' : 'rgba(0, 0, 0, 0.04)' 
                    }
                  }}
                >
                  {createDotIcon(cols)}
                </IconButton>
              );
            })}
          </Box>
        </Box>
      )}

      {/* 🚨 GLOBAL VIP NOTIFICATION - Above All Meetings */}
      {globalVipTracking.activeNotification?.show && (
        <Box sx={{ 
          position: 'relative', 
          mb: 3,
          p: 3,
          background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
          borderRadius: 3,
          border: '3px solid #ff4500',
          boxShadow: '0 6px 30px rgba(255, 69, 0, 0.5)',
          animation: 'globalVipSlideIn 0.6s ease-out',
          '@keyframes globalVipSlideIn': {
            '0%': { opacity: 0, transform: 'translateY(-30px) scale(0.9)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' }
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <PriorityHighIcon sx={{ 
                color: '#ffffff', 
                fontSize: '3rem', 
                mr: 3,
                animation: 'globalVipPulse 2s ease-in-out infinite',
                '@keyframes globalVipPulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.2)' },
                  '100%': { transform: 'scale(1)' }
                }
              }} />
              <Box>
                <Typography variant="h4" sx={{ 
                  color: '#ffffff', 
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  mb: 1
                }}>
                  🚨 VIP TEACHER CONNECTED!
                  {globalVipTracking.audioInterval && (
                    <Box component="span" sx={{ 
                      ml: 2, 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                      borderRadius: '20px',
                      fontSize: '1rem',
                      animation: 'globalSoundPulse 1.2s ease-in-out infinite',
                      '@keyframes globalSoundPulse': {
                        '0%': { opacity: 0.8, transform: 'scale(1)' },
                        '50%': { opacity: 1, transform: 'scale(1.1)' },
                        '100%': { opacity: 0.8, transform: 'scale(1)' }
                      }
                    }}>
                      🔊 SOUND PLAYING...
                    </Box>
                  )}
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: 'rgba(255, 255, 255, 0.95)',
                  mb: 1
                }}>
                  👤 {globalVipTracking.activeNotification.vipName}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 1
                }}>
                  📧 {globalVipTracking.activeNotification.vipEmail}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255, 255, 255, 0.85)'
                }}>
                  🏫 Meeting: {globalVipTracking.activeNotification.meetingName}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '0.85rem',
                  display: 'block',
                  mt: 1
                }}>
                  Connected at {globalVipTracking.activeNotification.timestamp?.toLocaleTimeString()}
                  {globalVipTracking.audioInterval && (
                    <Box component="span" sx={{ ml: 2, fontStyle: 'italic', fontWeight: 'bold' }}>
                      🎵 Audio repeating every 3 seconds - Click ✕ to stop
                    </Box>
                  )}
                  {pendingVipAlert && !audioReady && (
                    <Box component="span" sx={{ 
                      ml: 2, 
                      fontStyle: 'italic',
                      color: '#ffeb3b',
                      fontWeight: 'bold',
                      animation: 'globalAudioPrompt 1.5s ease-in-out infinite',
                      '@keyframes globalAudioPrompt': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                        '100%': { opacity: 1 }
                      }
                    }}>
                      🎧 Click anywhere to enable sound alerts!
                    </Box>
                  )}
                </Typography>
              </Box>
            </Box>
            
            <IconButton
              size="large"
              sx={{ 
                color: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s',
                width: 56,
                height: 56
              }}
              onClick={(e) => {
                e.stopPropagation();
                stopGlobalVipAlert();
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Active Meetings Grid */}
      {activeMeetings.length === 0 ? (
        <Alert severity="info" sx={{ 
          mb: 3,
          color: 'rgb(17, 17, 17)',
          backgroundColor: 'rgb(255, 255, 255)',
          '& .MuiAlert-icon': {
            fontSize: '59px',
            color: 'rgb(186, 191, 193)'
          }
        }}>
          <Typography variant="h6">No Active Meetings</Typography>
          <Typography>No live meetings detected at this time.</Typography>
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {activeMeetings.map((meeting) => {
            // 🚨 Get VIP teachers for this specific meeting
            const vipTeachersInMeeting = globalVipTracking.currentVipMeetings.get(meeting.id) || [];
            const hasVipPresent = vipTeachersInMeeting.length > 0;
            
            return (
              <Grid 
                item 
                xs={12} 
                sm={gridColumns === 2 ? 6 : 6}
                md={gridColumns === 2 ? 6 : gridColumns === 3 ? 4 : 3}
                lg={gridColumns === 2 ? 6 : gridColumns === 3 ? 4 : 3}
                xl={gridColumns === 2 ? 6 : gridColumns === 3 ? 4 : 3}
                key={meeting.id}
              >
                <SimpleMeetingCard 
                  meeting={meeting} 
                  isSelected={selectedMeetingId === meeting.id}
                  onSelect={() => setSelectedMeetingId(meeting.id)}
                  enhancedApi={enhancedApi}
                  compactMode={gridColumns > 3 || activeMeetings.length > 8}
                  // ✅ NEW: Assignment props
                  currentUser={user} // ✅ FIXED: Pass user from AuthContext
                  authLoading={authLoading}
                  token={token}
                  isAssigned={assignedMeetings.has(meeting.id)}
                  assignedUsers={meetingAssignments[meeting.id] || []}
                  onAssignToggle={handleAssignMeeting}
                  // ✅ NEW: WebSocket debug props
                  connectionStatus={connectionStatus}
                  socketEmit={socketEmit}
                  // 🚨 NEW: VIP props (from global state)
                  vipTeachers={vipTeachersInMeeting}
                  hasVipPresent={hasVipPresent}
                  // ⚙️ NEW: Refresh interval preference
                  refreshInterval={refreshInterval}
                  // 📱 SMS functionality
                  onStudentNameClick={handleStudentNameClick}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* SMS Dialog */}
      <Dialog 
        open={smsDialogOpen} 
        onClose={() => !sendingSms && setSmsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Send SMS to {selectedStudent?.first_name} {selectedStudent?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Phone: {selectedStudent?.phone1}
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select a message template:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {smsTemplates.map((template) => (
                <Button
                  key={template.id}
                  variant="outlined"
                  onClick={() => handleSendSms(template.id)}
                  disabled={sendingSms}
                  sx={{
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    p: 2,
                    textTransform: 'none',
                    position: 'relative'
                  }}
                >
                  {sendingTemplateId === template.id && (
                    <CircularProgress 
                      size={20} 
                      sx={{ 
                        position: 'absolute', 
                        left: 12, 
                        top: '50%', 
                        mt: '-10px' 
                      }} 
                    />
                  )}
                  <Box sx={{ ml: sendingTemplateId === template.id ? 4 : 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {template.template_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.template_content.replace(/{name}/g, selectedStudent?.first_name || '')}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmsDialogOpen(false)} disabled={sendingSms}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ENHANCED Meeting Card Component with Rate Limiting and Compact Mode + Assignment Feature
// 🚀 CRITICAL: React.memo prevents re-rendering when props don't change (solves UI freeze!)
const SimpleMeetingCard = React.memo(function SimpleMeetingCard({ 
  meeting, 
  isSelected, 
  onSelect, 
  enhancedApi, 
  getCachedRequest, 
  compactMode = false,
  // ✅ NEW: Assignment props
  currentUser, // user from AuthContext
  authLoading = false,
  token,
  isAssigned = false,
  assignedUsers = [],
  onAssignToggle,
  // ✅ NEW: WebSocket debug props
  connectionStatus,
  socketEmit,
  // 🚨 NEW: VIP props (from global state)
  vipTeachers = [],
  hasVipPresent = false,
  // ⚙️ NEW: Refresh interval preference
  refreshInterval = 14000,
  // 📱 SMS functionality
  onStudentNameClick
}) {
  const [participants, setParticipants] = useState([]);
  const [expectedStudents, setExpectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [dataSource, setDataSource] = useState('none');
  const [error, setError] = useState(null);

  // ✅ NEW: Loading states for individual copy buttons
  const [copyLoadingStates, setCopyLoadingStates] = useState(new Map());

  // ===== CLIPBOARD FUNCTIONALITY =====
  
  // Copy phone number to clipboard (memoized to prevent re-creation on every render)
  const copyToClipboard = useCallback(async (text) => {
    try {
      // ✅ Immediate synchronous copy
      await navigator.clipboard.writeText(text);
      toast.success(`📱 Phone copied: ${text}`, { duration: 2000 });
    } catch (err) {
      console.error('Failed to copy phone:', err);
      toast.error('❌ Failed to copy phone');
    }
  }, []);

  // <LockUnlockIcon /> NEW: Copy student credentials (email + idnumber) - memoized for performance
  const copyStudentCredentials = useCallback(async (student) => {
    const email = student.email || 'N/A';
    // Fix: API returns student_id at root level (from bot response), not nested
    const idnumber = student.student_id || student.idnumber || student.id_number || student.student_code || 'N/A';
    
    const credentials = `${email} ${idnumber}`;
    
    try {
      // ✅ Immediate synchronous copy
      await navigator.clipboard.writeText(credentials);
      toast.success(`🔑 Credentials copied: ${email} ${idnumber}`, { duration: 2000 });
    } catch (err) {
      console.error('Failed to copy credentials:', err);
      toast.error('❌ Failed to copy credentials');
    }
  }, []);

  // ✅ ENHANCED: Copy Zoom link by checking existing participant tokens (NO auto-registration) - memoized for performance
  const copyZoomLink = useCallback(async (student, meetingId) => {
    const studentKey = `${student.email}_${meetingId}`;
    let loadingToast = null;
    
    try {
      // Set loading state for this specific student
      setCopyLoadingStates(prev => new Map(prev.set(studentKey, true)));
      
      // Show immediate feedback that process started
      loadingToast = toast.loading(`🔍 Checking existing token for ${student.first_name}...`);
      
      const response = await api.get(`/api/bot/meetings/${meetingId}/generate-token/${encodeURIComponent(student.email)}`, {
        timeout: 20000 // Longer timeout for registration process
      });
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      loadingToast = null;
      
      if (response.data.success) {
        // Priority: Use workingFormat (with tk= token), fallback to fallbackFormat
        const linkToUse = response.data.generatedLinks.workingFormat || response.data.generatedLinks.fallbackFormat;
        
        if (linkToUse) {
          await navigator.clipboard.writeText(linkToUse);
          
          const method = response.data.success_method || 'generated';
          const formattedMethod = method.includes('existing_participant_token_SUCCESS') ? 'SUCCESS Token' : method;
          const hasPersonalToken = response.data.registrationData?.personalized_token ? '🎫' : '🔗';
          
          toast.success(`${hasPersonalToken} Zoom link copied for ${student.first_name}! (${formattedMethod})`);
        } else {
          toast.error(`❌ No Zoom link generated for ${student.first_name}`);
        }
      } else if (response.data.mustLoginFirst) {
        // 🚨 NEW: Handle "must login first" case
        const loginUrl = response.data.loginUrl || 'https://user.merimnaseminars.gr/login';
        await navigator.clipboard.writeText(loginUrl);
        
        toast(`${response.data.message || 'Student Must Login First'} - Login URL copied for ${student.first_name}`, {
          icon: '🔶',
          style: {
            background: 'rgb(255, 116, 48)',
            color: '#fff',
          }
        });
      } else {
        toast.error(`❌ Failed to generate Zoom link for ${student.first_name}`);
        console.error('Token check failed:', response.data.error || 'Unknown error');
      }
    } catch (error) {
      console.error('❌ Failed to copy Zoom link:', error);
      
      // ✅ Dismiss loading toast on error
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      
      if (error.code === 'ECONNABORTED') {
        toast.error(`⏰ Timeout generating link for ${student.first_name} - Try again`);
      } else {
        toast.error(`❌ Error copying link for ${student.first_name}`);
      }
    } finally {
      // Always clear loading state
      setCopyLoadingStates(prev => {
        const newMap = new Map(prev);
        newMap.delete(studentKey);
        return newMap;
      });
    }
  }, []); // Empty deps - all used functions/objects are stable (api, toast, debugService, setCopyLoadingStates)

  // ✅ NEW: AbortController ref to cancel pending requests
  const abortControllerRef = React.useRef(null);

  // 🚀 CRITICAL: NON-BLOCKING Fetch - Returns immediately, updates state when data arrives
  // 🎯 HYBRID APPROACH: Fetch full data ONLY for assigned meetings (reduces API calls by ~60%)
  const fetchMeetingData = (force = false, showLoading = false) => {
    // 🎯 NEW: Skip fetching if meeting is NOT assigned (preview mode)
    if (!isAssigned) {
      debugService.log(`⏸️ Skipping fetch for non-assigned meeting ${meeting.id} (preview mode)`);
      // Set preview state
      setTimeout(() => {
        setDataSource('preview_mode');
        setParticipants([]);
        setExpectedStudents([]);
        setLoading(false);
        setError(null);
      }, 0);
      return;
    }

    const now = Date.now();
    if (!force && (now - lastFetchTime < 3000)) { // 3 second rate limit for real-time updates
      debugService.log(`⏰ Rate limited: Meeting ${meeting.id} data fetch skipped`);
      return;
    }

    // ✅ Cancel any pending requests before starting new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // ✅ Only show loading spinner on initial load or forced refresh, not on periodic updates
    if (showLoading) {
      setTimeout(() => setLoading(true), 0);
    }
    setTimeout(() => setError(null), 0);
    
    // 🚀 CRITICAL: Fire requests and return immediately (don't await!)
    // This keeps the main thread free for user interactions
    (async () => {
      try {
        // 🚀 BATCH CONTROLLER: Priority-based fetching (normal priority for meeting cards)
        // 🤖 BOT-ONLY: No fallback - returns empty data on error to prevent 429
        const participantsPromise = enhancedApi.getBotParticipants(meeting.id, 5).catch((error) => {
          console.warn(`⚠️ Bot endpoint failed for meeting ${meeting.id}:`, error.message);
          // ✅ PERFORMANCE: Return empty data silently (debug log removed)
          return { data: { participants: [], count: 0, success: false, unavailable: true } };
        });

        // ENHANCED: Smart Group Matching and Student Fetching
        let studentsPromise;
        let groupId = meeting.matched_group_id;
        let groupName = meeting.matched_group_name;
        
        // Try auto-matching if no group assigned
        if (!groupId) {
          // Use extracted_group_name OR extract from meeting_name as fallback
          let extractedName = meeting.extracted_group_name;
          
          if (!extractedName && meeting.meeting_name && meeting.meeting_name.includes(' / ')) {
            extractedName = meeting.meeting_name.split(' / ')[0]?.trim();
          }
          
          if (extractedName) {
            try {
              const matchedGroup = await enhancedApi.findGroupByName(extractedName);
              if (matchedGroup) {
                groupId = matchedGroup.id;
                groupName = matchedGroup.group_name;
              }
            } catch (error) {
              console.warn('Auto-matching failed:', error);
            }
          }
        }
        
        if (groupId) {
          // 🚀 BATCH CONTROLLER: Normal priority for group students
          // 🤖 BOT-ONLY: No fallback - returns empty data on error
          studentsPromise = enhancedApi.getBotGroupStudents(groupId, 5).catch(error => {
            // ✅ PERFORMANCE: Silent error handling (debug logs removed)
            return { data: { data: [], count: 0, success: false } };
          });
        } else {
          studentsPromise = Promise.resolve({ data: [] });
        }
        
        // Store group info in meeting for UI display
        meeting.resolved_group_id = groupId;
        meeting.resolved_group_name = groupName;

        const [participantsRes, studentsRes] = await Promise.all([
          participantsPromise,
          studentsPromise
        ]);

        // Process participants data
        let participantsList = [];
        let newDataSource = dataSource;
        if (participantsRes.data && participantsRes.data.unavailable) {
          newDataSource = 'bot_unavailable';
        } else if (participantsRes.data && participantsRes.data.participants) {
          participantsList = participantsRes.data.participants;
          newDataSource = 'bot_system';
        }

        // Fix: Bot System returns {success: true, data: [...], ...}
        const studentsData = Array.isArray(studentsRes.data?.data) ? studentsRes.data.data : 
                            Array.isArray(studentsRes.data) ? studentsRes.data : [];

        // ✅ Update state when data arrives (deferred to prevent blocking)
        setTimeout(() => {
          setDataSource(newDataSource);
          setParticipants(participantsList);
          setExpectedStudents(studentsData);
          setLastFetchTime(now);
        }, 0);
        
        // ✅ PERFORMANCE: Debug logs removed - called per card every 14s (caused UI freeze with multiple cards)
        
      } catch (error) {
        // ✅ Ignore aborted requests (they were cancelled intentionally)
        if (error.name === 'AbortError' || error.message?.includes('abort')) {
          console.log(`🚫 Request aborted for meeting ${meeting.id} (interval changed)`);
          return;
        }
        
        console.error(`❌ Failed to fetch data for meeting ${meeting.id}:`, error);
        
        // ✅ Defer error state updates to prevent blocking user events
        setTimeout(() => {
          setError(error.message);
          setDataSource('error');
        }, 0);
      } finally {
        // ✅ Defer loading state update too
        setTimeout(() => setLoading(false), 0);
      }
    })(); // Execute IIFE immediately but don't await it
  };

  // Initial load and periodic updates (batch controller handles rate limiting)
  // 🎯 HYBRID APPROACH: Starts/stops fetching when assignment status changes
  useEffect(() => {
    // ✅ Initial load: Show loading spinner
    fetchMeetingData(false, true);
    
    // 🎯 NEW: Only set interval if meeting is assigned
    if (!isAssigned) {
      debugService.log(`⏸️ Skipping interval setup for non-assigned meeting ${meeting.id}`);
      return;
    }
    
    // ✅ Dynamic refresh interval based on user preference
    // Silent refresh without loading spinner to prevent UI blocking during copy operations
    const interval = setInterval(() => {
      fetchMeetingData(false, false); // Silent refresh
    }, refreshInterval); // ✅ User controllable: 14s/21s/35s
    
    return () => {
      clearInterval(interval);
      // ✅ Cancel any pending API requests when interval changes or component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [meeting.id, refreshInterval, isAssigned]); // 🎯 NEW: Added isAssigned to trigger re-run when assignment changes

  // ENHANCED Categorization with SMART Teacher Detection (prevents student-host misclassification)
  const { teachers, students, unknowns, connectedStudents, missingStudents, connectedTeachers } = useMemo(() => {
    // Safety checks: Ensure participants and expectedStudents are arrays
    const safeParticipants = Array.isArray(participants) ? participants : [];
    const safeExpectedStudents = Array.isArray(expectedStudents) ? expectedStudents : [];
    
    // ✅ PERFORMANCE: Removed per-participant debug logs (caused 5s UI freeze)
    // Only log summary at the end
    
    // 🎯 SMART Teacher Detection with Database Priority
    const teachers = safeParticipants.filter(p => {
      // PRIMARY: Check if explicitly marked as teacher by Bot System (DATABASE PRIORITY)
      if (p.participant_type === 'teacher') {
        return true;
      }
      
      // 🚀 FALLBACK: Check Zoom role ONLY for unknown participants (not students!)
      // This prevents misclassifying students who temporarily became host
      const isZoomTeacher = ['host', 'co-host', 'cohost'].includes(p.role?.toLowerCase());
      const isUnknownParticipant = !p.participant_type || p.participant_type === 'unknown';
      
      if (isZoomTeacher && isUnknownParticipant) {
        // Mark as teacher for consistency
        p.participant_type = 'teacher';
        p.detected_by = 'zoom_role_fallback';
        return true;
      }
      
      // 🔒 IMPORTANT: If participant is marked as 'student' in DB, NEVER override to teacher
      // even if they have host role (temporary host scenario)
      // (No action needed - just skip)
      
      return false;
    });
    
    const students = safeParticipants.filter(p => p.participant_type === 'student');
    
    // ENHANCED: Connected teachers (from expected students with role=teacher)
    const connectedTeachers = safeExpectedStudents
      .filter(expectedPerson => expectedPerson.role === 'teacher')
      .map(expectedTeacher => {
        const connectedParticipants = safeParticipants.filter(participant => 
          participant.participant_email?.toLowerCase() === expectedTeacher.email?.toLowerCase()
        );
        
        if (connectedParticipants.length > 0) {
          return {
            ...expectedTeacher,
            participants: connectedParticipants,
            deviceCount: connectedParticipants.length,
            isConnected: true
          };
        }
        return null;
      })
      .filter(Boolean);
    
    // ENHANCED: Connected students with device counting
    const connectedStudents = safeExpectedStudents
      .filter(expectedPerson => expectedPerson.role === 'student' || !expectedPerson.role)
      .map(expectedStudent => {
        const connectedParticipants = safeParticipants.filter(participant => 
          participant.participant_email?.toLowerCase() === expectedStudent.email?.toLowerCase()
        );
        
        if (connectedParticipants.length > 0) {
          return {
            ...expectedStudent,
            participants: connectedParticipants,
            deviceCount: connectedParticipants.length,
            isConnected: true
          };
        }
        return null;
      })
      .filter(Boolean);
    
    // ENHANCED: Missing students (expected but not connected)
    const missingStudents = safeExpectedStudents
      .filter(expectedPerson => expectedPerson.role === 'student' || !expectedPerson.role)
      .filter(expectedStudent => 
        !safeParticipants.some(participant => 
          participant.participant_email?.toLowerCase() === expectedStudent.email?.toLowerCase()
        )
      );
    
    // ✅ FIX: Unknown participants - exclude teachers, students, and connectedTeachers
    const unknowns = safeParticipants.filter(p => {
      // ✅ Must not be in the teachers array (check by participant_id or email)
      const isTeacher = teachers.some(t => 
        t.participant_id === p.participant_id || 
        (t.participant_email && p.participant_email && 
         t.participant_email.toLowerCase() === p.participant_email.toLowerCase())
      );
      if (isTeacher) {
        return false;
      }
      
      // ✅ Must not be in the students array
      const isStudent = students.some(s => 
        s.participant_id === p.participant_id || 
        (s.participant_email && p.participant_email && 
         s.participant_email.toLowerCase() === p.participant_email.toLowerCase())
      );
      if (isStudent) {
        return false;
      }
      
      // ✅ Must not be in the connectedTeachers array (expected teachers from group)
      const isConnectedTeacher = connectedTeachers.some(ct => 
        ct.email && p.participant_email && 
        ct.email.toLowerCase() === p.participant_email.toLowerCase()
      );
      if (isConnectedTeacher) {
        return false;
      }
      
      // ✅ Exclude if email matches any expected student from group
      const participantEmail = p.participant_email?.toLowerCase();
      if (participantEmail && safeExpectedStudents.length > 0) {
        const matchesExpected = safeExpectedStudents.some(expected => 
          expected.email?.toLowerCase() === participantEmail
        );
        if (matchesExpected) {
          return false; // This is a known person, not unknown
        }
      }
      
      return true; // This is truly unknown
    });
    
    // ✅ PERFORMANCE: Debug logs removed to prevent UI freeze
    // Summary: teachers.length, students.length, unknowns.length, connectedStudents.length, missingStudents.length
    
    return { teachers, students, unknowns, connectedStudents, missingStudents, connectedTeachers };
  }, [participants, expectedStudents]);


  // ENHANCED: Teacher detection based on actual expected teachers
  const hasTeacher = connectedTeachers.length > 0 || teachers.some(t => t.participant_type === 'teacher');
  const totalConnectedStudents = connectedStudents.length;
  const totalExpectedStudents = Array.isArray(expectedStudents) ? expectedStudents.filter(p => p.role === 'student' || !p.role).length : 0;
  const attendanceRate = totalExpectedStudents > 0 ? (totalConnectedStudents / totalExpectedStudents) * 100 : 0;

  return (
    <Card 
        elevation={isSelected ? 8 : 2}
        sx={{ 
          cursor: 'pointer',
          transition: 'all 0.3s',
          border: isSelected ? '2px solid #2088ff' : '1px solid #e0e0e0',
          '&:hover': { elevation: 6 },
          padding: '5px',
          marginBottom: '14px'
        }}
        onClick={onSelect}
      >
      <CardContent>
        {/* ENHANCED Header with Group Info */}
        <Box sx={{ mb: compactMode ? 1 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <Typography 
                variant={compactMode ? "subtitle1" : "h6"} 
                sx={{ 
                  fontWeight: 'bold', 
                  color: 'rgb(0, 57, 108)', 
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                  wordBreak: 'break-word'
                }}
              >
                {meeting.resolved_group_name || meeting.matched_group_name || 
                 meeting.extracted_group_name || 
                 meeting.meeting_name?.split(' / ')[0] ||
                 `Meeting ${meeting.zoom_meeting_id}`}
              </Typography>
              {!compactMode && meeting.meeting_name && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  {meeting.meeting_name.length > 50 
                    ? meeting.meeting_name.substring(0, 50) + '...' 
                    : meeting.meeting_name}
                </Typography>
              )}
            </Box>
            
            {/* ✅ NEW: Assignment Button - Moved to top-right */}
            {currentUser && !authLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <IconButton
                  onClick={(e) => onAssignToggle && onAssignToggle(meeting.id, e)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: isAssigned ? 'rgb(46, 125, 50)' : '#e0e0e0',
                    color: isAssigned ? '#ffffff' : '#666666',
                    padding: '2px',
                    '&:hover': {
                      bgcolor: isAssigned ? 'rgb(40, 110, 44)' : '#d0d0d0',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  title={isAssigned ? 'Click to unassign this meeting' : 'Click to assign this meeting'}
                >
                  <TickIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* ✅ NEW: Bot/API Data Source + Assigned Users - Same line, below subtitle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            {/* Left: Bot/API + Refresh */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Tooltip title={`Data Source: ${dataSource}`}>
                <Chip
                  size="small"
                  icon={<SmartToyIcon />}
                  label="BOT"
                  color="primary"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Tooltip>
              {loading && <CircularProgress size={16} />}
              <IconButton onClick={(e) => { e.stopPropagation(); fetchMeetingData(true, true); }} size="small" title="Manual refresh">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Right: Assigned Users */}
            {currentUser && !authLoading && assignedUsers.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {assignedUsers.map((user, idx) => (
                  <Chip
                    key={`${user.id}-${idx}`}
                    label={user.username}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      bgcolor: user.profile_color || '#0015ff',
                      color: '#ffffff',
                      '& .MuiChip-label': {
                        px: 0.8,
                        py: 0.2
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* ✅ NEW: Meeting Assignment Section */}
        {/* ✅ FIXED DEBUG: Only show when debugService is enabled */}
        {debugService.isEnabled() && (
          <Box sx={{ mb: 1, p: 1, bgcolor: 'rgba(255,255,0,0.1)', borderRadius: 1, fontSize: '0.7rem' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              🐛 DEBUG user: {currentUser ? `${currentUser.firstname || 'MISSING'} ${currentUser.lastname || 'MISSING'} (${currentUser.role})` : 'NULL'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              🐛 DEBUG profile_color: {currentUser ? `${currentUser.profile_color || 'MISSING'}` : 'NULL'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              🐛 DEBUG token: {token ? 'EXISTS' : 'NULL'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              🐛 DEBUG authLoading: {authLoading ? 'YES' : 'NO'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              🐛 DEBUG isAssigned: {isAssigned ? 'YES' : 'NO'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              🐛 DEBUG assignedUsers: {assignedUsers ? JSON.stringify(assignedUsers.map(u => `${u.username} [${u.profile_color}]`)) : 'NULL'}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
              🐛 DEBUG WebSocket: {connectionStatus} | emit: {!!socketEmit ? 'YES' : 'NO'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ 
            mb: 2,
            '& .MuiAlert-icon': {
              fontSize: '59px',
              color: 'rgb(186, 191, 193)'
            }
          }}>
            {error}
          </Alert>
        )}

        {/* 🎯 PREVIEW MODE: Show message for non-assigned meetings */}
        {dataSource === 'preview_mode' && !isAssigned && (
          <Box sx={{ 
            mt: 2, 
            p: 3, 
            textAlign: 'center',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 2,
            border: '2px dashed rgba(25, 118, 210, 0.3)'
          }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#2088ff', fontWeight: 'bold' }}>
              Preview Mode
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Click the assign button (✓) above to view full meeting details, participants, and students.
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Meeting ID: {meeting.zoom_meeting_id}
              {meeting.meeting_password && ` • Password: ${meeting.meeting_password}`}
            </Typography>
          </Box>
        )}

        {/* Teacher Status - Minimal Design */}
        {isAssigned && dataSource !== 'preview_mode' && (
        <>
        <Box sx={{ mb: 2 }}>
          {hasTeacher ? (
            <Alert severity="success" icon={false} sx={{ padding: '1px 16px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1.37 }}>
                Teacher Present
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" icon={false} sx={{ padding: '1px 16px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1.37 }}>
                No Teacher Present
              </Typography>
            </Alert>
          )}
        </Box>

        {/* ENHANCED Statistics - Focus on Students Only */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', fontSize: '1.9rem', color: 'rgb(46, 125, 50)' }}>
                {totalConnectedStudents}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgb(46, 125, 50)' }}>Connected Students</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', fontSize: '1.9rem', color: missingStudents.length === 0 ? 'rgb(223, 223, 223)' : 'rgb(226, 21, 51)' }}>
                {missingStudents.length}
              </Typography>
              <Typography variant="body2" sx={{ color: missingStudents.length === 0 ? 'rgb(223, 223, 223)' : 'rgb(226, 21, 51)' }}>Missing Students</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', fontSize: '1.9rem', color: Math.round(attendanceRate) === 100 ? 'rgb(46, 125, 50)' : 'rgb(0, 57, 108)' }}>
                {Math.round(attendanceRate)}%
              </Typography>
              <Typography variant="body2" sx={{ color: Math.round(attendanceRate) === 100 ? 'rgb(46, 125, 50)' : 'rgb(0, 57, 108)' }}>Attendance Rate</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Enhanced Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Student Attendance</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {totalConnectedStudents}/{totalExpectedStudents} ({Math.round(attendanceRate)}%)
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={attendanceRate}
            color={attendanceRate >= 80 ? 'success' : attendanceRate >= 60 ? 'warning' : 'error'}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>

        {/* 🚨 VIP TEACHERS - Simplified Display (Global Detection) */}
        {hasVipPresent && vipTeachers.length > 0 && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: 'rgb(206, 96, 30)', 
            borderRadius: 2, 
            border: '2px solid #f57c00',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* VIP Header - Simplified */}
            <Typography variant="h6" sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center', 
              color: '#ffffff',
              fontSize: '1rem',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              🎯 VIP TEACHERS IN THIS MEETING
              <Box sx={{
                ml: 1,
                width: 10,
                height: 10,
                backgroundColor: '#4caf50',
                borderRadius: '50%'
              }} />
            </Typography>
            
            {/* VIP Teachers List - Simplified */}
            <Box>
              {vipTeachers.map((vipTeacher, idx) => (
                <Box key={`vip-${idx}`} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 0.5,
                  p: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 1
                }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#4caf50',
                    mr: 1
                  }} />
                  <Typography variant="body2" sx={{ 
                    color: '#ffffff', 
                    fontWeight: 'bold',
                    textShadow: '1px 1px 1px rgba(0,0,0,0.5)'
                  }}>
                    🎯 {vipTeacher.first_name} {vipTeacher.last_name}
                    <Box component="span" sx={{ 
                      ml: 1, 
                      fontSize: '0.69rem',
                      opacity: 0.8
                    }}>
                      - {vipTeacher.email}
                    </Box>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ENHANCED: Always Visible Lists with Pulsing Indicators */}
        <Box sx={{ mt: 2 }}>
          {/* Teachers - Minimal Design */}
          {(connectedTeachers.length > 0 || teachers.length > 0) && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgb(229, 242, 255)', borderRadius: 2, border: '1px solid #90caf9' }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', color: 'rgb(0, 57, 108)', fontSize: '1.05rem' }}>
                Teachers
              </Typography>
              <Box>
                {/* Expected Teachers */}
                {connectedTeachers.map((teacher, idx) => (
                  <Box key={`expected-${idx}`} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      bgcolor: '#4caf50',
                      mr: 1
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgb(0, 57, 108)' }}>
                      {teacher.deviceCount > 1 ? `(${teacher.deviceCount}) ` : ''}
                      {teacher.first_name} {teacher.last_name} - {teacher.email}
                    </Typography>
                  </Box>
                ))}
                {/* Raw Zoom Teachers */}
                {teachers.filter(t => !connectedTeachers.some(ct => 
                  ct.email?.toLowerCase() === t.participant_email?.toLowerCase()
                )).map((teacher, idx) => (
                  <Box key={`raw-${idx}`} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      bgcolor: '#4caf50',
                      mr: 1
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgb(0, 57, 108)' }}>
                      {teacher.participant_name} - {teacher.participant_email || 'No email detected'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          
          {/* Missing Students - Only show if there are missing students */}
          {missingStudents.length > 0 && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: 'rgb(249, 236, 241)', 
            borderRadius: 2, 
            border: '1px solid rgb(227, 203, 211)' 
          }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center',
              color: 'rgb(226, 21, 51)',
              fontSize: '1.05rem'
            }}>
              Missing Students ({missingStudents.length})
            </Typography>
            <Box>
              {missingStudents.map((student, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: 'rgb(229, 56, 80)',
                    mr: 1,
                    animation: 'blink 1.5s ease-in-out infinite',
                    '@keyframes blink': {
                      '0%, 50%': { opacity: 1 },
                      '51%, 100%': { opacity: 0.3 }
                    }
                  }} />
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgb(225, 11, 42)', 
                          flex: 1,
                          cursor: 'pointer',
                          '&:hover': { 
                            textDecoration: 'underline',
                            color: '#d32f2f'
                          }
                        }}
                        onClick={() => onStudentNameClick && onStudentNameClick(student)}
                        title="Click to send SMS"
                      >
                        {student.first_name} {student.last_name} - {student.email}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* <LockUnlockIcon /> NEW: Copy student credentials button */}
                        <Box 
                          sx={{ 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#f57c00', // Orange/yellow color for the key
                            fontSize: '1.2rem',
                            '&:hover': { 
                              opacity: 0.7,
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s'
                          }}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); copyStudentCredentials(student); }}
                          title="Copy student credentials (email + ID number)"
                        >
                          <LockUnlockIcon />
                        </Box>
                        
                        {/* ✅ ENHANCED: Copy button with loading state */}
                        <Box 
                          sx={{ 
                            cursor: copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 0.6 : 1,
                            '&:hover': { 
                              opacity: copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 0.6 : 0.7 
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (!copyLoadingStates.get(`${student.email}_${meeting.id}`)) {
                              copyZoomLink(student, meeting.id);
                            }
                          }}
                          title={copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 
                            "Checking for existing token..." : 
                            "Copy Zoom link if student has existing token (no auto-registration)"
                          }
                        >
                          {copyLoadingStates.get(`${student.email}_${meeting.id}`) ? '⏳' : <ShareIcon />}
                        </Box>
                      </Box>
                    </Box>
                    {student.phone1 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'rgb(223, 26, 62)',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                          fontSize: '0.69rem',
                          margin: 0,
                          display: 'block'
                        }}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); copyToClipboard(student.phone1); }}
                        title="Click to copy phone"
                      >
                        {student.phone1}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          )}
          
          {/* Connected Students - Only show if there are connected students */}
          {connectedStudents.length > 0 && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: 'rgb(249, 255, 248)', 
            borderRadius: 2, 
            border: '1px solid rgb(210, 240, 211)' 
          }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center',
              color: 'rgb(63, 96, 64)',
              fontSize: '1.05rem'
            }}>
              Connected Students ({connectedStudents.length}/{totalExpectedStudents})
            </Typography>
            <Box>
              {connectedStudents.map((student, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: '#4caf50',
                    mr: 1
                  }} />
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgb(67, 91, 68)', 
                          flex: 1,
                          cursor: 'pointer',
                          '&:hover': { 
                            textDecoration: 'underline',
                            color: '#388e3c'
                          }
                        }}
                        onClick={() => onStudentNameClick && onStudentNameClick(student)}
                        title="Click to send SMS"
                      >
                        {student.deviceCount > 1 ? `(${student.deviceCount}) ` : ''}
                        {student.first_name} {student.last_name} - {student.email}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* <LockUnlockIcon /> NEW: Copy student credentials button */}
                        <Box 
                          sx={{ 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#f57c00', // Orange/yellow color for the key
                            fontSize: '1.2rem',
                            '&:hover': { 
                              opacity: 0.7,
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s'
                          }}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); copyStudentCredentials(student); }}
                          title="Copy student credentials (email + ID number)"
                        >
                          <LockUnlockIcon />
                        </Box>
                        
                        {/* ✅ ENHANCED: Copy button with loading state */}
                        <Box 
                          sx={{ 
                            cursor: copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 0.6 : 1,
                            '&:hover': { 
                              opacity: copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 0.6 : 0.7 
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (!copyLoadingStates.get(`${student.email}_${meeting.id}`)) {
                              copyZoomLink(student, meeting.id);
                            }
                          }}
                          title={copyLoadingStates.get(`${student.email}_${meeting.id}`) ? 
                            "Checking for existing token..." : 
                            "Copy Zoom link if student has existing token (no auto-registration)"
                          }
                        >
                          {copyLoadingStates.get(`${student.email}_${meeting.id}`) ? '⏳' : <ShareIcon />}
                        </Box>
                      </Box>
                    </Box>
                    {student.phone1 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'rgb(9, 40, 11)',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                          fontSize: '0.69rem',
                          margin: 0,
                          display: 'block'
                        }}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); copyToClipboard(student.phone1); }}
                        title="Click to copy phone"
                      >
                        {student.phone1}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          )}
          
          {/* Other Participants - Only show if there are unknowns */}
          {unknowns.length > 0 && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: 'rgb(255, 245, 246)', 
            borderRadius: 2, 
            border: '1px solid rgb(236, 210, 218)' 
          }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center',
              color: 'rgb(223, 26, 62)',
              fontSize: '1.05rem'
            }}>
              Other Participants ({unknowns.length})
            </Typography>
            <Box>
              {unknowns.map((participant, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: 'rgb(229, 56, 80)',
                    mr: 1
                  }} />
                  <Typography variant="body2" sx={{ color: 'rgb(223, 26, 62)' }}>
                    {participant.participant_name} - {participant.participant_email || 'No email'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          )}
        </Box>
        </>
        )}
        
        {/* Meeting Details - Moved to bottom */}
        <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Meeting ID: {meeting.zoom_meeting_id}
            {meeting.meeting_password && ` • Password: ${meeting.meeting_password}`}
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Last Update: {new Date(lastFetchTime).toLocaleTimeString()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // 🚀 CRITICAL: Custom comparison - only re-render if these props change
  return (
    prevProps.meeting.id === nextProps.meeting.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.compactMode === nextProps.compactMode &&
    prevProps.isAssigned === nextProps.isAssigned &&
    prevProps.hasVipPresent === nextProps.hasVipPresent &&
    prevProps.refreshInterval === nextProps.refreshInterval &&
    JSON.stringify(prevProps.assignedUsers) === JSON.stringify(nextProps.assignedUsers) &&
    JSON.stringify(prevProps.vipTeachers) === JSON.stringify(nextProps.vipTeachers)
  );
});

export default SimpleLiveMonitoring;
// Cache bust: Sat Nov  1 2025 - Replaced inline notifications with react-hot-toast (top-right notifications)
// Cache bust: Thu Sep  4 08:37:15 PM EEST 2025
// Cache bust: Thu Sep  4 08:51:36 PM EEST 2025
// Cache bust: Thu Sep  4 10:17:25 PM EEST 2025 - TypeError Fixes + CSP WebSocket
// ENHANCED Version: Group Matching + Device Counting + Always Open Lists + Meeting Details
// Cache bust: Thu Sep  4 09:55:13 PM EEST 2025
// Cache bust: Thu Sep  4 11:02:26 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Thu Sep  4 11:23:38 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Thu Sep  4 11:43:27 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 12:59:55 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 01:13:47 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 01:29:15 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 01:39:20 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 01:43:45 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 01:51:36 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 02:06:50 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 02:40:13 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 03:22:13 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 04:19:46 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 12:26:17 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: ZOOM LINK GENERATOR IMPLEMENTED - Phone numbers and Zoom link generation with clipboard functionality
// Cache bust: Fri Sep  5 01:48:20 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 02:24:17 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 04:07:33 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 04:27:50 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 05:11:56 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 05:56:14 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 06:02:54 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 06:23:31 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 07:13:46 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 07:38:50 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 07:50:59 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 08:32:02 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: REGISTRATION-BASED ZOOM LINKS WITH PERSONALIZED tk= TOKENS - Enhanced loading states and notification display
// Cache bust: Fri Sep  5 08:59:28 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 09:02:59 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 09:38:04 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 10:03:26 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 10:43:06 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 11:14:08 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Fri Sep  5 11:25:11 PM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: Sat Sep  6 07:58:25 AM EEST 2025 - Always Visible Lists with Pulsing Indicators
// Cache bust: NEW FEATURE - Added yellow key button (<LockUnlockIcon />) to copy student credentials (email + idnumber)
// Cache bust: NEW FEATURE - Added Debug Service integration - Key console.log messages now controlled by Settings toggle
// Cache bust: DEBUG SYSTEM COMPLETED - All debug console.log messages now use debugService.log() and respect Debug ON/OFF setting
// Cache bust: HYBRID APPROACH IMPLEMENTED - Nov 3, 2025
// 🎯 Fetch full data (participants + students) ONLY for assigned meetings (reduces API calls by ~60%)
// 🌍 VIP Monitoring continues to run globally for ALL meetings (lightweight, priority 1)
// 📋 Preview Mode for non-assigned meetings with "Click assign to view details" message
// Cache bust: PERFORMANCE FIX - Nov 8, 2025
// ✅ Added cacheTime (5min) + keepPreviousData to React Query - instant navigation return
// ✅ Sequential VIP monitoring with setTimeout(0) - eliminates 5s UI freeze on clicks
